/**
 * Where a tournament is in its life, derived from its data (console spec §4).
 * Pure: plain data in, a report out, the clock passed in. The shell draws the
 * phase rail from `boxes`, fills `active` navy, and puts `nextUp` at the top of
 * Overview.
 */
import type { ConflictWarning } from "@alpha/engine";
import type { TournamentStatus } from "@alpha/shared";
import { divisionBracket } from "./bundle";
import { dayKey } from "./gameDay";
import type { Bundle, Division, Game } from "./types";

/**
 * What the signed-in caller may do on this tournament. Three grants reach the
 * console — global admin, tournamentAdmin, tournamentScorer — and the API
 * enforces an explicit table of them, so the shell derives this once and
 * threads it down rather than asking the role question in every page.
 * Offering a control the API will refuse is the failure this exists to prevent.
 */
export interface Caps {
  /**
   * Organization-wide: the venue library link. A tournament director picks
   * venues from the chips; they cannot open the editor whose every write 403s.
   */
  manage: boolean;
  /**
   * Running this event: divisions, entries, the signup queue, seeding, the
   * bracket, staff, and delete. Held by a global admin and by the director of
   * this tournament — that is the whole grant now.
   */
  runEvent: boolean;
  /** The tournament record itself — status, details, banner, registration window. */
  editEvent: boolean;
  /** Recording results: `PUT .../games/:gid/score`. Held by both scoped roles. */
  score: boolean;
  /** Building the schedule: create a game, fill its slots, set when and where, delete. */
  schedule: boolean;
}

export interface PhaseInput {
  bundle: Bundle;
  /** Null unless the caller may read the queue and it has loaded. */
  pendingRegistrations: number | null;
  conflicts: Map<string, ConflictWarning[]>;
  caps: Caps;
  now: Date;
}

export type PhaseKey = "setup" | "teams" | "schedule" | "scores" | "playoffs";

export interface PhaseBox {
  key: PhaseKey;
  label: string;
  /** "3 pending", "12 / 20", "4 today" — or null when there is nothing to say. */
  figure: string | null;
  done: boolean;
  attention: boolean;
}

export interface NextUp {
  /** "overview" for a status transition, or for nothing at all. */
  phase: PhaseKey | "overview";
  message: string;
  action: "go" | "publish" | "complete" | "none";
}

export interface PhaseReport {
  /** Only the phases the caller may open, in rail order. */
  boxes: PhaseBox[];
  /** The box Next up points at — filled navy. Null for a status rule or nothing. */
  active: PhaseKey | null;
  nextUp: NextUp;
}

export const PHASE_ORDER: readonly PhaseKey[] = ["setup", "teams", "schedule", "scores", "playoffs"];

export const PHASE_LABEL: Record<PhaseKey, string> = {
  setup: "Setup",
  teams: "Teams",
  schedule: "Schedule",
  scores: "Scores",
  playoffs: "Playoffs",
};

/**
 * Which capability opens each phase (console spec §3.1, §3.5). Setup opens on
 * `editEvent` because its first two cards — the event record and its
 * registration window — are that grant's; the cards below them gate on their
 * own capability inside the page.
 */
export const PHASE_CAP: Record<PhaseKey, keyof Caps> = {
  setup: "editEvent",
  teams: "runEvent",
  schedule: "schedule",
  scores: "score",
  playoffs: "runEvent",
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * The games that could be scored right now: still scheduled, with both sides
 * known. A bracket game's sides come from the resolver, not the record, so a
 * semifinal waiting on a quarterfinal is not playable however it is stored.
 */
export function playableGames(bundle: Bundle): Game[] {
  const resolvedSides = new Map<string, boolean>();
  for (const d of bundle.divisions) {
    if (!bundle.games.some((g) => g.divisionId === d.id && g.type === "bracket")) continue;
    for (const r of divisionBracket(bundle, d.id).games) {
      resolvedSides.set(r.gameId, Boolean(r.home.teamId && r.away.teamId));
    }
  }
  return bundle.games.filter(
    (g) =>
      g.status === "scheduled" &&
      (g.type === "pool" ? Boolean(g.homeTeamId && g.awayTeamId) : resolvedSides.get(g.id) === true),
  );
}

const isScheduled = (g: Game): boolean => Boolean(g.scheduledAt && (g.venueId || g.field));
const isFinished = (g: Game): boolean => g.status === "final" || g.status === "forfeit";
const isSettled = (g: Game): boolean => isFinished(g) || g.status === "canceled";

/** Everything the figures and rules read, computed once. */
function facts(input: PhaseInput) {
  const { bundle, conflicts, now, pendingRegistrations: pending } = input;
  const { tournament: t, divisions, pools, entries, games } = bundle;
  const tz = t.timezone;

  const poolsOf = (d: Division) => pools.filter((p) => p.divisionId === d.id);
  const bracketOf = (d: Division) => games.filter((g) => g.divisionId === d.id && g.type === "bracket");
  const poolGamesOf = (d: Division) => games.filter((g) => g.divisionId === d.id && g.type === "pool");

  const divisionsWithoutPools = divisions.filter((d) => poolsOf(d).length === 0);
  // Only in a division that has pools: an entry in a poolless division has
  // nowhere to go yet, and that is rule 2's problem, not rule 7's.
  const unpooled = entries.filter(
    (e) => e.poolId === null && pools.some((p) => p.divisionId === e.divisionId),
  );
  const scheduled = games.filter(isScheduled);
  // Conflicts are keyed by game and a warning names two games, so it appears
  // under both; the set counts it once.
  const conflictCount = new Set([...conflicts.values()].flat()).size;
  const emptyPools = pools.filter(
    (p) =>
      entries.filter((e) => e.poolId === p.id).length >= 2 &&
      !games.some((g) => g.type === "pool" && g.poolId === p.id),
  );

  const playable = playableGames(bundle);
  const today = dayKey(now.toISOString(), tz);
  const playableToday = playable.filter((g) => g.scheduledAt && dayKey(g.scheduledAt, tz) === today);
  const pastPlayable = playable.filter(
    (g) =>
      g.scheduledAt !== undefined &&
      Date.parse(g.scheduledAt) + t.config.gameLengthMinutes * 60_000 < now.getTime(),
  );

  const toSeed = divisions.filter((d) => {
    const pg = poolGamesOf(d);
    return (
      pg.length > 0 &&
      pg.every(isSettled) &&
      pg.some(isFinished) &&
      !d.lockedSeeds &&
      !unpooled.some((e) => e.divisionId === d.id)
    );
  });
  const toBracket = divisions.filter((d) => d.lockedSeeds && bracketOf(d).length === 0);
  const withBracket = divisions.filter((d) => bracketOf(d).length > 0);
  const bracketsDone = withBracket.filter((d) => bracketOf(d).every(isFinished));
  const anyLocked = divisions.some((d) => d.lockedSeeds);

  return {
    t,
    divisions,
    entries,
    games,
    pending,
    divisionsWithoutPools,
    unpooled,
    scheduled,
    conflictCount,
    emptyPools,
    playableToday,
    pastPlayable,
    toSeed,
    toBracket,
    withBracket,
    bracketsDone,
    anyLocked,
    finishedCount: games.filter(isFinished).length,
    allSettled: games.length > 0 && games.every(isSettled),
    divisionName: (divisionId: string) => divisions.find((d) => d.id === divisionId)?.name ?? "",
  };
}

type Facts = ReturnType<typeof facts>;

interface Rule {
  when: (f: Facts) => boolean;
  message: (f: Facts) => string;
  phase: PhaseKey | "overview";
  action: NextUp["action"];
  requires: keyof Caps | "any";
}

/**
 * Console spec §4.3, in order. The first rule that holds AND that the caller
 * may act on wins.
 */
const RULES: Rule[] = [
  { when: (f) => f.t.status === "archived", message: () => "Archived — unarchive to make changes", phase: "overview", action: "none", requires: "any" },
  // Complete outranks every working rule below: the organizer closed the event
  // — past the readiness strip, if anything was unfinished — and a closed
  // event that still nagged about seeding would never say it was done.
  { when: (f) => f.t.status === "complete", message: () => "All done — archive it whenever you like", phase: "overview", action: "none", requires: "any" },
  { when: (f) => f.divisions.length === 0, message: () => "Add your first division", phase: "setup", action: "go", requires: "runEvent" },
  { when: (f) => f.divisionsWithoutPools.length > 0, message: (f) => `Add pools to ${f.divisionsWithoutPools[0]!.name}`, phase: "setup", action: "go", requires: "runEvent" },
  { when: (f) => !f.t.location, message: () => "Add the city so teams can find this event", phase: "setup", action: "go", requires: "editEvent" },
  { when: (f) => !f.t.registration, message: () => "Set the registration window", phase: "setup", action: "go", requires: "editEvent" },
  { when: (f) => f.t.status === "draft", message: () => "Publish the tournament", phase: "overview", action: "publish", requires: "editEvent" },
  { when: (f) => (f.pending ?? 0) > 0, message: (f) => `Approve ${plural(f.pending ?? 0, "pending registration")}`, phase: "teams", action: "go", requires: "runEvent" },
  { when: (f) => f.unpooled.length > 0, message: (f) => `Put ${plural(f.unpooled.length, "team")} in a pool`, phase: "teams", action: "go", requires: "runEvent" },
  { when: (f) => f.emptyPools.length > 0, message: (f) => `Build the schedule for ${f.divisionName(f.emptyPools[0]!.divisionId)} · ${f.emptyPools[0]!.name}`, phase: "schedule", action: "go", requires: "schedule" },
  { when: (f) => f.games.length > f.scheduled.length, message: (f) => `Set a time and field on ${plural(f.games.length - f.scheduled.length, "game")}`, phase: "schedule", action: "go", requires: "schedule" },
  { when: (f) => f.conflictCount > 0, message: (f) => `Fix ${plural(f.conflictCount, "schedule conflict")}`, phase: "schedule", action: "go", requires: "schedule" },
  { when: (f) => f.playableToday.length > 0, message: (f) => `Score today's ${plural(f.playableToday.length, "game")}`, phase: "scores", action: "go", requires: "score" },
  { when: (f) => f.pastPlayable.length > 0, message: (f) => `Record ${plural(f.pastPlayable.length, "finished game")}`, phase: "scores", action: "go", requires: "score" },
  { when: (f) => f.toSeed.length > 0, message: (f) => `Finalize seeding for ${f.toSeed[0]!.name}`, phase: "playoffs", action: "go", requires: "runEvent" },
  { when: (f) => f.toBracket.length > 0, message: (f) => `Create the ${f.toBracket[0]!.name} bracket`, phase: "playoffs", action: "go", requires: "runEvent" },
  { when: (f) => f.allSettled && f.finishedCount > 0 && f.t.status === "published", message: () => "Mark the tournament complete", phase: "overview", action: "complete", requires: "editEvent" },
];

/**
 * Each phase's `done` is the negation of every Next up rule that points at it,
 * so a box is never checked and navy at once.
 */
function boxFor(key: PhaseKey, f: Facts): PhaseBox {
  const label = PHASE_LABEL[key];
  switch (key) {
    case "setup":
      return {
        key,
        label,
        figure: f.divisions.length ? `${f.divisions.length} div` : "Not started",
        done:
          f.divisions.length > 0 &&
          f.divisionsWithoutPools.length === 0 &&
          Boolean(f.t.location) &&
          f.t.registration !== null,
        attention: false,
      };
    case "teams": {
      const pending = f.pending ?? 0;
      return {
        key,
        label,
        figure:
          pending > 0
            ? `${pending} pending`
            : f.unpooled.length > 0
              ? `${f.unpooled.length} unpooled`
              : `${f.entries.length} teams`,
        done: f.entries.length > 0 && pending === 0 && f.unpooled.length === 0,
        attention: pending > 0 || f.unpooled.length > 0,
      };
    }
    case "schedule": {
      const conflicts = f.conflictCount > 0 ? ` · ${plural(f.conflictCount, "conflict")}` : "";
      return {
        key,
        label,
        figure: f.games.length === 0 ? "No games" : `${f.scheduled.length} / ${f.games.length}${conflicts}`,
        done:
          f.games.length > 0 &&
          f.scheduled.length === f.games.length &&
          f.conflictCount === 0 &&
          f.emptyPools.length === 0,
        attention: f.conflictCount > 0,
      };
    }
    case "scores":
      return {
        key,
        label,
        figure:
          f.playableToday.length > 0
            ? `${f.playableToday.length} today`
            : f.pastPlayable.length > 0
              ? `${f.pastPlayable.length} unscored`
              : "—",
        done: f.games.length > 0 && f.playableToday.length === 0 && f.pastPlayable.length === 0,
        attention: f.pastPlayable.length > 0,
      };
    case "playoffs":
      return {
        key,
        label,
        figure: f.anyLocked ? `${f.withBracket.length} / ${f.divisions.length}` : "—",
        done:
          f.divisions.length > 0 &&
          f.bracketsDone.length === f.divisions.length &&
          f.toSeed.length === 0 &&
          f.toBracket.length === 0,
        attention: false,
      };
  }
}

export function derivePhases(input: PhaseInput): PhaseReport {
  const { caps } = input;
  const f = facts(input);

  const boxes = PHASE_ORDER.filter((key) => caps[PHASE_CAP[key]]).map((key) => boxFor(key, f));

  const rule = RULES.find((r) => (r.requires === "any" || caps[r.requires]) && r.when(f));
  const scoreOnly = !caps.runEvent && !caps.editEvent && !caps.schedule;
  const nextUp: NextUp = rule
    ? { phase: rule.phase, message: rule.message(f), action: rule.action }
    : {
        phase: "overview",
        message: scoreOnly ? "Nothing to score right now" : "Nothing waiting on you",
        action: "none",
      };

  return {
    boxes,
    active: nextUp.phase === "overview" ? null : nextUp.phase,
    nextUp,
  };
}

/** The status word the rail's Overview cell shows. */
export const STATUS_WORD: Record<TournamentStatus, string> = {
  draft: "Draft",
  published: "Published",
  complete: "Complete",
  archived: "Archived",
};
